import React from "react"
import CalendarTutorial from "../../components/calendar-tutorial"

const tutorial = {
  quickAnswer: {
    question: "How can families follow the classroom week?",
    answer:
      "Create and check the week\u2019s events, then publish a read-only calendar link. Families use that link to view the plan. After changing your original calendar, republish and ask families to refresh the same link.",
  },
  title: "Create a Weekly Classroom Calendar and Share It with Parents",
  description:
    "Follow a Year 3 example: add lessons, try Magic Generate, publish a read-only calendar, and update the same link when plans change.",
  intro:
    "Give parents a clear view of the classroom week. In this walkthrough, you’ll build a small Year 3 schedule, review events created with Magic Generate, and publish a read-only link that you can update later.",
  companion: {
    url: "/downloads/classroom-week-example.csv",
    label: "Download the example week as an editable CSV",
    note:
      "Open in a spreadsheet or print it as a planning sheet. This is not a calendar import file; choose actual dates in the app.",
  },
  rows: [
    ["Monday", "9:00 am", "Reading", "30 min"],
    ["Tuesday", "9:00 am", "Maths", "45 min"],
    ["Wednesday", "10:30 am", "PE", "60 min"],
    ["Thursday", "11:00 am", "Library", "30 min"],
    ["Friday", "2:00 pm", "Assembly", "30 min"],
  ],
  prompt:
    "For the week starting Monday [YYYY-MM-DD], create these events:\nMonday 9am Reading for 30 minutes\nTuesday 9am Maths for 45 minutes\nWednesday 10:30am PE for 1 hour\nThursday 11am Library for 30 minutes\nFriday 2pm Assembly for 30 minutes",
  steps: [
    {
      title: "1. Create a blank classroom calendar",
      paragraphs: [
        "Open School Calendar and choose New blank calendar. Open the calendar settings to give it a recognisable title, such as “Year 3 — Weekly Plan”, and a short description for families. Navigate to the week you want to plan.",
      ],
    },
    {
      title: "2. Add a lesson manually or try Magic Generate",
      paragraphs: [
        "For a manual start, select an activity and click a calendar time slot. Open the event details to adjust its title, start time, and duration.",
        "To create the example week with AI, open Magic Generate and send the prompt above with a specific start date. Use a blank calendar for this example, or leave out any lesson you already added manually.",
      ],
    },
    {
      title: "3. Review the events before adding them",
      paragraphs: [
        "Check every suggested date, time, activity name, and duration. Pay particular attention to the chosen week. When the result matches your plan, select Add to calendar.",
        "Use the calendar to review the whole week. Adjust any event details before you share the plan with families.",
      ],
    },
    {
      title: "4. Publish a calendar parents can view",
      paragraphs: [
        "Select Publish calendar. Review the title, description, and events that viewers will see. If you want the shared view to require a password, set one before publishing.",
        "Publish and copy the link. Open it in another browser or on your phone to check the parent view. Viewers can read the published calendar without editing your original. Share that link through the channel your classroom normally uses.",
      ],
    },
    {
      title: "5. Update the plan using the same link",
      paragraphs: [
        "Suppose library time moves from 11am to 11:30am. Edit that event, return to Publish calendar, and select Republish. The shared URL stays the same.",
        "Parents see the updated version when they reopen or refresh the link. Editing your local calendar alone does not publish the change, so make republishing part of your routine.",
      ],
    },
    {
      title: "6. Give families a clear introduction",
      paragraphs: [
        "You could introduce the link with: “Here is our classroom plan for next week. Please use this link to check lesson times and activities. We’ll republish it when the plan changes.”",
        "Keep the calendar focused on information families need: lesson times, library visits, PE sessions, assemblies, and other shared activities.",
      ],
    },
  ],
  cta: "Create next week’s classroom calendar",
  relatedUrl: "/use-cases/after-school-activity-schedule-ai/",
  relatedTitle: "Build an After-School Activity Schedule with AI",
}

export default function TutorialPage() {
  return <CalendarTutorial {...tutorial} />
}
